import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IdCard } from 'lucide-react';
import { ChildRecords } from '@/components/ChildRecords';
import { StatusBadge } from '@/components/live';
import { referenceValues } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';
import { RELATION_TYPES, relationName } from '@/lib/relations';
import { KycRecordDrawer } from '@/screens/parts/KycRecordDrawer';

export interface RightsHolderRow {
  id: string; holder_code: string | null; plot_id: string; holder_role: string; full_name: string; relationship_type_id: string | null;
  relationship_label: string | null; relation_type: string | null; father_or_spouse_name: string | null; gender: string | null;
  date_of_birth: string | null; mobile: string | null; address: string | null; pin_code: string | null; photo_document_id: string | null;
  share_pct: number | null; receives_payment: boolean; person_party_id: string | null; remarks: string | null; status: string;
  kyc_count: number; kyc_verified: number;
}

/** Which people a tenure calls for: a jointly held or inherited plot's family, a sharecropped plot's two sides. */
export type RightsMode = 'family' | 'sharecrop';

const FAMILY_CODES = ['SPOUSE', 'SON', 'DAUGHTER', 'FATHER', 'MOTHER', 'BROTHER', 'SISTER', 'SON_IN_LAW', 'DAUGHTER_IN_LAW', 'FAMILY_OTHER'];
const ROLES: Record<RightsMode, Array<{ id: string; label: string }>> = {
  family: [{ id: 'family_member', label: 'Family member' }, { id: 'co_owner', label: 'Co-owner (recorded on the title)' }],
  sharecrop: [{ id: 'sharecropper', label: 'Sharecropper (bargadar / adhiar)' }, { id: 'landowner', label: 'Landowner' }],
};
const GENDERS = [{ id: 'female', label: 'Female' }, { id: 'male', label: 'Male' }, { id: 'other', label: 'Other' }, { id: 'undisclosed', label: 'Prefer not to say' }];

/** A plot's land tenure, as the tab to show: family for joint or inherited land, sharecrop for sharecropping. */
export function rightsModeOf(tenureCode: string | null | undefined): RightsMode | null {
  if (!tenureCode) return null;
  if (tenureCode === 'SHARECROP') return 'sharecrop';
  if (tenureCode === 'JOINT_FAMILY' || tenureCode.startsWith('INHERITED')) return 'family';
  return null;
}

/**
 * The people with a right in the plot besides the farmer (owner, 2026-09-28: "child table for
 * recording the family, share cropper, and so on to capture photo, kyc and so on like the worker?
 * this can be tabbed separately and displayed on selection"). Shown as its own tab when the plot's
 * tenure is joint, inherited or sharecropping. Each person has their own KYC; they are named on,
 * and sign, the plot's family members' or sharecropping consent.
 */
export function RightsHolders({ plotId, mode }: { plotId: string; mode: RightsMode }) {
  const queryClient = useQueryClient();
  const [kycFor, setKycFor] = useState<RightsHolderRow | null>(null);
  const relationships = useQuery({ queryKey: ['ref', 'relationship_type'], queryFn: () => referenceValues('relationship_type'), enabled: isSupabaseConfigured });
  const relationOptions = (relationships.data ?? [])
    .filter((row) => (mode === 'family' ? FAMILY_CODES.includes(row.value_code) : ['SHARECROPPER', 'LANDOWNER', 'FAMILY_OTHER'].includes(row.value_code)))
    .map((row) => ({ id: row.id, label: row.label }));
  const age = (dob: string | null) => (dob ? Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 86400000)) : null);

  return (
    <>
      <ChildRecords<RightsHolderRow>
        addLabel={mode === 'family' ? 'Add family member' : 'Add landowner or sharecropper'}
        defaults={{ holder_role: ROLES[mode][0]!.id, receives_payment: false }}
        emptyText={mode === 'family'
          ? 'No family member recorded yet. Add everyone who holds a share in this plot: they sign its family members\' consent.'
          : 'Neither side recorded yet. Add the landowner and the sharecropper: both sign the plot\'s sharecropping consent.'}
        extra={(row) => (
          <button onClick={() => setKycFor(row)} title="This person's own KYC documents" type="button">
            <IdCard size={14} /> KYC {row.kyc_count ? `(${row.kyc_verified}/${row.kyc_count} verified)` : ''}
          </button>
        )}
        fields={[
          { key: 'holder_role', label: 'Holds the plot as', type: 'select', options: ROLES[mode], required: true, section: 'Person' },
          { key: 'full_name', label: 'Full name', type: 'text', required: true },
          { key: 'relationship_type_id', label: mode === 'family' ? 'Relationship to the farmer' : 'Relationship', type: 'select', options: relationOptions,
            required: mode === 'family' },
          { key: 'relation_type', label: 'Relation (as on ID)', type: 'select', options: RELATION_TYPES },
          { key: 'father_or_spouse_name', label: 'Father / spouse name', type: 'text' },
          { key: 'gender', label: 'Gender', type: 'select', options: GENDERS },
          { key: 'date_of_birth', label: 'Date of birth', type: 'date', max: new Date().toISOString().slice(0, 10) },
          { key: 'photo_document_id', label: 'Photo', type: 'file', accept: 'image/*' },
          { key: 'mobile', label: 'Mobile', type: 'text', inputMode: 'tel', placeholder: '10 digits', section: 'Contact & share' },
          { key: 'address', label: 'Address', type: 'textarea', wide: true },
          { key: 'pin_code', label: 'PIN code', type: 'text', inputMode: 'numeric' },
          { key: 'share_pct', label: mode === 'family' ? 'Share in the plot (%)' : 'Share of produce / proceeds (%)', type: 'number', min: '0', max: '100',
            hint: 'All shares on the plot together cannot pass 100 %.' },
          { key: 'receives_payment', label: 'Paid their share directly', type: 'check' },
          { key: 'remarks', label: 'Remarks', type: 'textarea', wide: true },
        ]}
        filters={{ plot_id: plotId }}
        invalidate={['party_consents']}
        intro={mode === 'family'
          ? 'The family members who hold this plot with the farmer. Each has their own photo and KYC, and signs the family members\' consent.'
          : 'The landowner and the sharecropper, with the share each takes. Both sign the sharecropping consent.'}
        orderBy={{ column: 'created_at', ascending: true }}
        ownerId={plotId}
        parent={{ plot_id: plotId }}
        screenKey="plot.register"
        summary={(row) => ({
          title: <>{row.full_name} <small className="muted">{ROLES[mode].find((role) => role.id === row.holder_role)?.label ?? row.holder_role}</small></>,
          detail: [row.relationship_label, relationName(row.relation_type, row.father_or_spouse_name),
            age(row.date_of_birth) != null ? `${age(row.date_of_birth)} yrs` : null, row.mobile,
            row.share_pct != null ? `${row.share_pct} % share${row.receives_payment ? ', paid directly' : ''}` : null, row.holder_code].filter(Boolean).join(' · '),
          badge: row.kyc_verified > 0 ? <StatusBadge label="KYC verified" tone="green" />
            : <StatusBadge label={row.kyc_count ? 'KYC pending' : 'No KYC'} tone="amber" />,
        })}
        table="ops.plot_rights_holders"
        title={mode === 'family' ? 'Family members' : 'Landowner and sharecropper'}
        validate={(form) => ({
          mobile: form.mobile && !/^(\+91)?[6-9]\d{9}$/.test(String(form.mobile).replace(/[\s-]/g, '')) ? 'Ten digits, starting 6-9.' : undefined,
          pin_code: form.pin_code && !/^[1-9]\d{5}$/.test(String(form.pin_code)) ? 'Six digits, not starting with 0.' : undefined,
          share_pct: form.share_pct !== '' && form.share_pct != null && (Number(form.share_pct) < 0 || Number(form.share_pct) > 100) ? 'Between 0 and 100.' : undefined,
        })}
        view="plot_rights_holders"
      />
      {kycFor?.person_party_id && (
        <KycRecordDrawer onClose={() => { setKycFor(null); void queryClient.invalidateQueries({ queryKey: ['plot_rights_holders'] }); }}
                         open partyId={kycFor.person_party_id} partyLabel={kycFor.full_name} roleContext="rights_holder" />
      )}
    </>
  );
}
